import type {ClientContactNode} from './CompanyViewPage';

// A blank first name is null, '', or whitespace-only.
const isBlank = (value: string | null | undefined): boolean => (value ?? '').trim() === '';

// The same collator options `useSort.sort` passes, so the panel orders names the way the rest
// of the app does. We reuse the comparison, not `useSort` itself: its `_getValue` coerces a
// blank to '' and its `updateSort` is a three-state cycle, neither of which fits here.
const compareText = (a: string | null | undefined, b: string | null | undefined): number =>
	`${a ?? ''}`.localeCompare(`${b ?? ''}`, undefined, {sensitivity: 'base', numeric: true});

/**
 * Ascending order for two named contacts: first name, tie-broken on last name then email.
 * First name alone is not a total order — the Name cell renders `First Last`, so two contacts
 * sharing a first name would otherwise appear in refetch-dependent order and visibly reshuffle
 * between visits. Callers negate the result for Z-A.
 */
export const compareClientContactsByName = (a: ClientContactNode, b: ClientContactNode): number =>
	compareText(a.firstName, b.firstName) || compareText(a.lastName, b.lastName) || compareText(a.email, b.email);

/**
 * Sorts a copy of `contacts` on first name. Contacts with a blank first name are partitioned
 * out before comparing and appended in their original order — in BOTH directions. Left to the
 * comparator they would sort *first* ascending, because `''.localeCompare('x') === -1`.
 */
export const sortClientContacts = (contacts: ClientContactNode[], ascending: boolean): ClientContactNode[] => {
	const named = contacts.filter(c => !isBlank(c.firstName));
	const blanks = contacts.filter(c => isBlank(c.firstName));
	const direction = ascending ? 1 : -1;
	return [...named.sort((a, b) => direction * compareClientContactsByName(a, b)), ...blanks];
};
